import { Account, type IncomeSplitConfig } from '@fruitful/income-split';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    LabelRegular,
    RadioInput,
    Sheet,
    Switch,
    XStack,
    YStack,
} from 'smoothie';
import { v4 as uuidv4 } from 'uuid';

import { incomeSplitAtom } from '../../../banking/state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../../banking/state/SpacesAtoms';
import { isAccountValidAsIncomeSplitSource } from '../../../banking/utils/IncomeSplitUtils';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';

interface IncomeSplitSettingsDraft {
    onlyExecuteOnPaychecks: boolean;
    accounts: IncomeSplitConfig['accounts'];
}

interface IncomeSplitSettingsSheetProps {
    isOpen: boolean;
    onClose: () => void;
    incomeSplit: IncomeSplitConfig;
}

const GroupHeader = ({
    title,
    subtitle,
}: {
    title: string;
    subtitle: string;
}) => (
    <YStack gap={4} padding={16} backgroundColor="$gray200">
        <BodyMediumRegular>{title}</BodyMediumRegular>
        <LabelRegular color="$gray1000">{subtitle}</LabelRegular>
    </YStack>
);

const IncomeSplitSettingsSheet = ({
    isOpen,
    onClose,
    incomeSplit,
}: IncomeSplitSettingsSheetProps) => {
    const user = useAuthedUser();
    const accounts = useAtomValue(bankAccountDisplayDetails);
    const setIncomeSplit = useSetAtom(incomeSplitAtom);

    const [draft, setDraft] = useState<IncomeSplitSettingsDraft>({
        onlyExecuteOnPaychecks: incomeSplit.onlyExecuteOnPaychecks,
        accounts: incomeSplit.accounts,
    });

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDraft({
            onlyExecuteOnPaychecks: incomeSplit.onlyExecuteOnPaychecks,
            accounts: incomeSplit.accounts,
        });
    }, [isOpen, incomeSplit]);

    const eligibleAccounts = accounts.filter((account) =>
        isAccountValidAsIncomeSplitSource(user, account),
    );

    const handleIncomeTypeChange = (onlyPaychecks: boolean) => {
        setDraft((prev) => ({
            ...prev,
            onlyExecuteOnPaychecks: onlyPaychecks,
        }));
    };

    const handleAccountToggle = (accountId: string, checked: boolean) => {
        setDraft((prev) => {
            let updatedAccounts = prev.accounts;
            if (checked) {
                updatedAccounts = [
                    ...updatedAccounts,
                    {
                        id: uuidv4(),
                        paycheckRoutingConfigId: incomeSplit.id as string,
                        accountId,
                    },
                ];
            } else {
                updatedAccounts = updatedAccounts.filter(
                    (a) => a.accountId !== accountId,
                );
            }
            return {
                ...prev,
                accounts: updatedAccounts,
            };
        });
    };

    const handleCancel = () => {
        onClose();
    };

    const handleSave = () => {
        setIncomeSplit({
            ...incomeSplit,
            onlyExecuteOnPaychecks: draft.onlyExecuteOnPaychecks,
            accounts: draft.accounts,
        });
        onClose();
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={handleCancel}
            title="Income settings"
            showCloseButton={true}
        >
            <YStack
                paddingTop={12}
                width="100%"
                maxWidth={500}
                alignSelf="center"
                gap={24}
            >
                <YStack gap={16}>
                    <YStack borderRadius={24} overflow="hidden">
                        <GroupHeader
                            title="Income type"
                            subtitle="Apply rules to all income or only to paychecks"
                        />
                        <XStack
                            padding={16}
                            gap={16}
                            alignItems="center"
                            justifyContent="space-between"
                            backgroundColor="$gray100"
                            onPress={() => {
                                handleIncomeTypeChange(false);
                            }}
                            cursor="pointer"
                        >
                            <YStack flexShrink={1} gap={4}>
                                <BodyMediumRegular>
                                    All income
                                </BodyMediumRegular>
                                <BodySmallRegular color="$gray1000">
                                    Any deposits, such as paychecks, interest
                                    earned, or cash back. Excludes ACH returns
                                    and reversals, provisional credits, and
                                    transfers initiated from Fruitful.
                                </BodySmallRegular>
                            </YStack>
                            <RadioInput
                                isSelected={!draft.onlyExecuteOnPaychecks}
                                onPress={() => {
                                    handleIncomeTypeChange(false);
                                }}
                            />
                        </XStack>
                        <XStack
                            padding={16}
                            gap={16}
                            alignItems="center"
                            justifyContent="space-between"
                            backgroundColor="$gray100"
                            onPress={() => {
                                handleIncomeTypeChange(true);
                            }}
                            cursor="pointer"
                        >
                            <YStack flexShrink={1} gap={4}>
                                <BodyMediumRegular>
                                    Paychecks only
                                </BodyMediumRegular>
                                <BodySmallRegular color="$gray1000">
                                    Any deposits that look like paychecks (we do
                                    our best to identify paychecks based on
                                    available transaction details)
                                </BodySmallRegular>
                            </YStack>
                            <RadioInput
                                isSelected={draft.onlyExecuteOnPaychecks}
                                onPress={() => {
                                    handleIncomeTypeChange(true);
                                }}
                            />
                        </XStack>
                    </YStack>

                    <YStack borderRadius={24} overflow="hidden">
                        <GroupHeader
                            title="Accounts eligible for Income Split"
                            subtitle="Apply rules to income deposited into these accounts"
                        />
                        {eligibleAccounts.map((account) => (
                            <YStack
                                key={account.bankAccountId}
                                backgroundColor="$gray100"
                            >
                                <Account
                                    account={account}
                                    footer={
                                        <Switch
                                            isChecked={draft.accounts.some(
                                                (a) =>
                                                    a.accountId ===
                                                    account.bankAccountId,
                                            )}
                                            onCheckedChange={(
                                                checked: boolean,
                                            ) => {
                                                handleAccountToggle(
                                                    account.bankAccountId,
                                                    checked,
                                                );
                                            }}
                                            style={{
                                                transformOrigin: 'right',
                                            }}
                                        />
                                    }
                                />
                            </YStack>
                        ))}
                    </YStack>
                </YStack>

                <XStack gap={12}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Cancel"
                        onPress={handleCancel}
                        buttonProps={{ flexShrink: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSave}
                        buttonProps={{ flexShrink: 1 }}
                    />
                </XStack>
            </YStack>
        </Sheet>
    );
};

export { IncomeSplitSettingsSheet };
